import { useQuery } from '@apollo/client'
import { Typography,Box } from '@mui/material'
import { ColumnsType } from 'antd/es/table'
import { DatePicker, Select, Space, Row, Col, Tooltip } from 'antd'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { GET_BATCH_DATA, GET_LEADERBOARD_DATA, GET_USER_BY_ID } from '../../Graphql/Queries'
import Card from '../Cards/Card'
import DataTable from '../Datatable'
import Stats from '../Stats'
import TableHeading from '../TableHeading'
import dayjs from 'dayjs'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate } from 'react-router-dom'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const { RangePicker } = DatePicker
const { Option } = Select

const formatSecondsToMinSec = (totalSeconds: number) => {
  if (!totalSeconds || isNaN(totalSeconds)) return "0 sec";
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  if (mins > 0) {
    return `${mins} min${mins > 1 ? "s" : ""} ${secs > 0 ? `& ${secs} sec${secs > 1 ? "s" : ""}` : ""}`;
  }
  return `${secs} sec${secs > 1 ? "s" : ""}`;
};

const AdminDashboard = () => {
  const { organization, userId } = useSelector((state: any) => state.auth)
  const [tableData, setTableData] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [selectedBatch, setSelectedBatch] = useState<string>('all')
  const [dateRange, setDateRange] = useState<any>(null)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const navigate = useNavigate()

  // Search states
  const [searchName, setSearchName] = useState<string>('');
  const [searchBatch, setSearchBatch] = useState<string>('');
  const [searchRank, setSearchRank] = useState<number | null>(null);
  const [searchTrainings, setSearchTrainings] = useState<number | null>(null);
  const [searchCompleted, setSearchCompleted] = useState<number | null>(null);
  const [searchAccuracy, setSearchAccuracy] = useState<number | null>(null);

  const { data: userRolesData, loading: userRolesLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: userId },
    skip: !userId,
  })
  
  const { data: batchData, loading: batchLoading } = useQuery(GET_BATCH_DATA, {
    variables: { orgId: organization },
    skip: !organization,
  })

  const userWithRoles = userRolesData?.userWithRolesById
  const isBatchAdmin = userWithRoles?.roles?.some((role: any) => role.roleCode === 'BATCH_ADMIN')
  const batchIds = userWithRoles?.batchIds || []

  const filteredBatches = batchData?.getBatchesByOrgId?.filter((batch: any) => {
  if (isBatchAdmin) {
    return batchIds.includes(batch._id)
  }
  return true // Show all batches for other roles
})

  const { loading: leaderBoardLoading, refetch } = useQuery<any>(
    GET_LEADERBOARD_DATA,
    {
      variables: {
        orgId: organization,
        batchId: selectedBatch === 'all' ? null : selectedBatch,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD') || dayjs().startOf('month').format('YYYY-MM-DD'),
        endDate: dateRange?.[1]?.format('YYYY-MM-DD') || dayjs().endOf('month').format('YYYY-MM-DD'),
        name: searchName || undefined,
        accuracy: searchAccuracy ? Number(searchAccuracy) : undefined,
        totalTrainings: searchTrainings ? Number(searchTrainings) : undefined,
        trainingCompleted: searchCompleted ? Number(searchCompleted) : undefined,
        rank: searchRank ? Number(searchRank) : undefined,
        batchName: searchBatch || undefined,
      },
      onCompleted(data) {
        if (data?.leaderBoard?.length > 0) {
          setTableData(data.leaderBoard.map((ranker: any, index: number) => ({
            key: index,
            userId: ranker.userId,
            userName: ranker.name,
            rank: ranker.rank,
            noOfTrainings: ranker.totalTrainings,
            trainingCompleted: ranker.trainingCompleted,
            accuracy: ranker.accuracy,
            batchName: ranker.batchName,
            averageTimeTaken: ranker.avgTimeTaken, // <-- add this
            completionPercent: ranker.completionPercent, // <-- add this
          })))
        } else {
          setTableData([])
        }
      },
    }
  )

  // Add the GET_USER_BY_ID query
  const { data: userData, loading: userLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: selectedUser?.userId },
    skip: !selectedUser?.userId,
    onCompleted(data) {
      if (data?.userWithRolesById) {
        // Navigate to user details page with the user data
        navigate(`/users/${selectedUser.userId}`, { state: { userData: data.userWithRolesById } })
      }
    },
    onError(error) {
      console.error('Error fetching user details:', error)
    }
  })

  const handleRowClick = (record: any) => {
    navigate(`/users/${record.userId}`, { state: { userId: record.userId } });
  }

  useEffect(() => {
    refetch();
  }, [searchName, searchBatch, searchRank, searchTrainings, searchCompleted, searchAccuracy, selectedBatch, dateRange]);

  const columns: ColumnsType<any> = [
    {
      title: <TableHeading heading='Name' setSearch={setSearchName} setPage={setCurrentPage} />, 
      dataIndex: 'userName',
      key: '1',
      width: 160,
      ellipsis: true,
      render: (text) => <Typography variant='body1'>{text}</Typography>,
    },
    {
      title: <TableHeading heading='Batch' setSearch={setSearchBatch} setPage={setCurrentPage} />, 
      dataIndex: 'batchName',
      key: '2',
      width: 160,
      render: (text) => (
        <Tooltip title={text} arrow>
          <Typography variant='body1' sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 160 }}>{text || 'N/A'}</Typography>
        </Tooltip>
      ),
    },
    {
      title: <TableHeading heading='Rank' setSearch={setSearchRank} setPage={setCurrentPage} />, 
      dataIndex: 'rank',
      key: '3',
      width: 90,
      render: (text) => <Typography variant='body1'>{text}</Typography>,
    },
    {
      title: <TableHeading heading='Number of trainings' setSearch={setSearchTrainings} setPage={setCurrentPage} />, 
      dataIndex: 'noOfTrainings',
      key: '4',
      width: 180,
      render: (text) => <Typography variant='body1'>{text}</Typography>,
    },
    {
      title: <TableHeading heading='Trainings completed' setSearch={setSearchCompleted} setPage={setCurrentPage} />, 
      dataIndex: 'trainingCompleted',
      key: '5',
      width: 180,
      render: (text) => <Typography variant='body1'>{text}</Typography>,
    },
    {
      title: <TableHeading heading='Accuracy' setSearch={setSearchAccuracy} setPage={setCurrentPage} />, 
      dataIndex: 'accuracy',
      key: '6',
      width: 90,
      render: (value) => <Typography variant='body1'>{`${parseFloat(value).toFixed(2)}%`}</Typography>,
    },
    {
      title: <TableHeading heading='Average Time Taken' setSearch={setSearchAccuracy} setPage={setCurrentPage} />,
      dataIndex: 'averageTimeTaken',
      key: '7',
      width: 180,
      render: (value) => <Typography variant='body1'>{formatSecondsToMinSec(Number(value))}</Typography>,
    },
    {
      title: <TableHeading heading='Completion %' setSearch={setSearchAccuracy} setPage={setCurrentPage} />,
      dataIndex: 'completionPercent',
      key: '8',
      width: 140,
      render: (value) => <Typography variant='body1'>{value !== undefined ? `${parseFloat(value).toFixed(2)}%` : 0}</Typography>,
    },
  ]

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col span={8}>
          <Stats text="Total Users" number={tableData.length} />
        </Col>
        <Col span={12} offset={4}>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              padding: '10px',
              border: '1px solid #d9d9d9',
              borderRadius: '8px',
              backgroundColor: 'rgb(248 248 248)',
              height: '70px', 
              marginTop:'50px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px',flex: 1  }}>
              <CalendarMonthIcon style={{ color: '#5c5a55' }} /> {/* Black calendar icon */}
              <RangePicker
                onChange={(dates) => setDateRange(dates)}
                value={dateRange || [dayjs().startOf('month'), dayjs().endOf('month')]}
                allowClear
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
                style={{ flex: 1 }}
                suffixIcon={null}
              />
            </div>
            {/* Divider */}
            <div style={{ width: '1px', height: '32px', backgroundColor: '#d9d9d9' }}></div>
            <Select
              defaultValue="all"
              style={{ flex: 1 }}
              onChange={setSelectedBatch}
              value={selectedBatch}
            >
              <Option value="all">All Batches</Option>
              {filteredBatches?.map((batch: any) => (
    <Select.Option key={batch._id} value={batch._id}>
      {batch.cohortName}
    </Select.Option>
  ))}
            </Select>
          </div>
        </Col>
      </Row>

{/* Info icon and formula explanation */}
      <Box display="flex" alignItems="center" mb={1} justifyContent="flex-end">
        <Tooltip
          title={
            <span>
              <b>Rank Score Formula:</b><br />
              <code>
                Score = 0.5 × (accuracy / 100) + 0.3 × (completionPercent / 100) + 0.2 × (1 - (avgTimeTaken / Tmax))
              </code>
              <br />
              <span>
                <b>Note:</b> Tmax is the maximum average time taken among all users.
              </span>
            </span>
          }
          placement="left"
        >
          <InfoOutlinedIcon sx={{ mr: 1, cursor: 'pointer', color: '#888' }} />
        </Tooltip>
      </Box>

      <Card>
        <DataTable
          loading={leaderBoardLoading}
          tableData={tableData}
          showCheck={false}
          columnData={columns}
          scrollX='max-content'
          onRow={(record) => handleRowClick(record)}
        />
      </Card>
    </>
  )
}

export default AdminDashboard
